import React from 'react';
import { flow } from 'lodash';
import { useDispatch } from 'react-redux';

import toaster from '@giro/shared-store/toaster';
import getApiPaymentLinksService from '@giro/shared-services/pdv/getApiPaymentLinks.service';
import getApiPaymentLinksIdService from '@giro/shared-services/pdv/getApiPaymentLinksId.service';
import postApiPaymentLinksService from '@giro/shared-services/pdv/postApiPaymentLinks.service';
import postApiPaymentLinksIdDispatchService from '@giro/shared-services/pdv/postApiPaymentLinksIdDispatch.service';
import postApiPaymentLinksIdRefreshStatusService from '@giro/shared-services/pdv/postApiPaymentLinksIdRefreshStatus.service';

import {
  createInitialForm,
  FINAL_STATUSES,
  initialFilters,
  NOT_SENDABLE_STATUSES,
  SUMMARY_GROUPS,
} from '../constants/paymentLinks.constants';
import {
  formatPhoneNumber,
  getStatusConfig,
  isSafeHttpUrl,
  onlyDigits,
  withEffectivePaymentLinkStatus,
} from '../utils/paymentLinks.utils';
import {
  downloadLinkQrCodeImage,
  downloadLinkSummaryFile,
  exportLinksCsv,
} from '../utils/paymentLinksExport.utils';

// Estado, efeitos e ações da tela de links de pagamento. A view só consome o retorno.
const usePaymentLinks = () => {
  const dispatch = useDispatch();
  const loadRequestVersion = React.useRef(0);
  const detailRequestVersion = React.useRef(0);
  const activeDetailId = React.useRef<string | null>(null);
  const creatingRequest = React.useRef(false);
  const sendingRequest = React.useRef(false);
  const refreshingRequest = React.useRef(false);
  const loadingMoreRequest = React.useRef<number | null>(null);

  const dispatchRedux = {
    show: flow(toaster.action.show, dispatch),
  };

  const [tab, setTab] = React.useState<'list' | 'create'>('list');
  const [actionMenu, setActionMenu] = React.useState<{
    anchorEl: HTMLElement | null;
    item: any;
  }>({ anchorEl: null, item: null });
  const [form, setForm] = React.useState(createInitialForm);
  const [filters, setFilters] = React.useState(initialFilters);
  const [quickStatusGroupKey, setQuickStatusGroupKey] = React.useState('');
  const [searchInput, setSearchInput] = React.useState('');
  const [links, setLinks] = React.useState<any[]>([]);
  const [totalAvailableLinks, setTotalAvailableLinks] = React.useState(0);
  const [page, setPage] = React.useState(0);
  const [rowsPerPage, setRowsPerPage] = React.useState(5);
  const [loading, setLoading] = React.useState(false);
  const [loadingMore, setLoadingMore] = React.useState(false);
  const [creating, setCreating] = React.useState(false);
  const [refreshingId, setRefreshingId] = React.useState('');
  const [detail, setDetail] = React.useState<any>(null);
  const [detailOpen, setDetailOpen] = React.useState(false);
  const [detailLoading, setDetailLoading] = React.useState(false);
  const [sendDialog, setSendDialog] = React.useState<any>(null);
  const [sending, setSending] = React.useState(false);
  const [sendForm, setSendForm] = React.useState({
    type: 'Whatsapp',
    value: '',
  });

  const showToast = (message: string, variant: any = 'success') => {
    dispatchRedux.show({ message, variant });
  };

  React.useEffect(
    () => () => {
      ++loadRequestVersion.current;
      ++detailRequestVersion.current;
      activeDetailId.current = null;
    },
    []
  );

  const loadLinks = React.useCallback(async () => {
    const requestVersion = ++loadRequestVersion.current;
    setLoading(true);
    setLoadingMore(false);
    const [success, data] = await getApiPaymentLinksService({
      ...filters,
      limit: 50,
      offset: 0,
    });
    if (requestVersion !== loadRequestVersion.current) return;
    setLoading(false);

    if (!success) {
      setLinks([]);
      setTotalAvailableLinks(0);
      showToast('Falha ao buscar links de pagamento.', 'error');
      return;
    }

    const results = (data?.results || []).map(withEffectivePaymentLinkStatus);
    setLinks(results);
    setTotalAvailableLinks(
      Number(data?.metadata?.resultset?.count ?? results.length)
    );
  }, [filters.search, filters.status, filters.type]);

  // A mutation may finish after the user has changed the list filters.
  const latestLoadLinks = React.useRef(loadLinks);
  latestLoadLinks.current = loadLinks;

  const loadMoreLinks = async () => {
    if (
      loading ||
      loadingMore ||
      loadingMoreRequest.current === loadRequestVersion.current ||
      links.length >= totalAvailableLinks
    )
      return;

    const requestVersion = loadRequestVersion.current;
    loadingMoreRequest.current = requestVersion;
    setLoadingMore(true);
    const [success, data] = await getApiPaymentLinksService({
      ...filters,
      limit: 50,
      offset: links.length,
    });
    if (loadingMoreRequest.current === requestVersion)
      loadingMoreRequest.current = null;
    if (requestVersion !== loadRequestVersion.current) return;
    setLoadingMore(false);

    if (!success) {
      showToast('Falha ao buscar mais links de pagamento.', 'error');
      return;
    }

    const results = (data?.results || []).map(withEffectivePaymentLinkStatus);
    setLinks((current) => {
      const byId = new Map(current.map((item) => [item.id, item]));
      results.forEach((item) => byId.set(item.id, item));
      return Array.from(byId.values());
    });
    setTotalAvailableLinks(
      Number(data?.metadata?.resultset?.count ?? links.length + results.length)
    );
  };

  React.useEffect(() => {
    loadLinks();
  }, [loadLinks]);

  const refreshLinks = React.useCallback(async () => {
    if (filters.search !== searchInput) {
      setFilters((state) => ({ ...state, search: searchInput }));
      return;
    }

    await loadLinks();
  }, [filters.search, loadLinks, searchInput]);

  // Debounce da busca: evita recarregar a lista a cada tecla digitada.
  React.useEffect(() => {
    const timer = setTimeout(() => {
      setFilters((state) =>
        state.search === searchInput ? state : { ...state, search: searchInput }
      );
    }, 450);
    return () => clearTimeout(timer);
  }, [searchInput]);

  // Paginação visual sobre os lotes carregados da API.
  React.useEffect(() => {
    setPage(0);
  }, [filters.search, filters.status, filters.type, rowsPerPage]);

  const updateForm = (field: string, value: any) => {
    setForm((state) => ({ ...state, [field]: value }));
  };

  const updateFilter = (field: string, value: any) => {
    if (field === 'status') {
      setQuickStatusGroupKey('');
    }
    setFilters((state) => ({ ...state, [field]: value }));
  };

  const clearFilters = () => {
    setSearchInput('');
    setQuickStatusGroupKey('');
    setFilters(initialFilters);
  };

  const hasActiveFilters =
    Boolean(filters.search) ||
    Boolean(filters.status) ||
    Boolean(filters.type) ||
    Boolean(quickStatusGroupKey);

  const downloadLinkSummary = (item: any) => {
    if (downloadLinkSummaryFile(item)) showToast('Resumo baixado.');
  };

  const downloadQrCode = (item: any) => {
    if (downloadLinkQrCodeImage(item)) showToast('QR Code baixado.');
  };

  const validateForm = () => {
    if (form.type === 'amount') {
      const amount = Number(form.amount);
      if (!Number.isFinite(amount) || amount <= 0 || amount > 999999.99) {
        showToast('Informe um valor entre R$ 0,01 e R$ 999.999,99.', 'error');
        return false;
      }
    }

    if (form.type === 'boleto') {
      const barcode = onlyDigits(form.barcode);
      if (![44, 47, 48].includes(barcode.length)) {
        showToast(
          'Informe um código de barras ou linha digitável válida.',
          'error'
        );
        return false;
      }
    }

    if (form.dispatch && !form.destinationValue) {
      showToast('Informe o destino para envio.', 'error');
      return false;
    }

    if (
      form.dispatch &&
      form.destinationType === 'Email' &&
      !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.destinationValue)
    ) {
      showToast('Informe um e-mail válido.', 'error');
      return false;
    }

    if (form.dispatch && form.destinationType === 'Whatsapp') {
      const phone = onlyDigits(form.destinationValue);
      if (phone.length < 10 || phone.length > 11) {
        showToast('Informe um WhatsApp com DDD.', 'error');
        return false;
      }
    }

    if (form.description.length > 500) {
      showToast('A descrição deve ter no máximo 500 caracteres.', 'error');
      return false;
    }

    if (form.externalReference.length > 150) {
      showToast('A referência deve ter no máximo 150 caracteres.', 'error');
      return false;
    }

    if (form.dueDate) {
      const dueDate = new Date(`${form.dueDate}T00:00:00`);
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      if (Number.isNaN(dueDate.getTime()) || dueDate < today) {
        showToast('O vencimento não pode estar no passado.', 'error');
        return false;
      }
    }

    return true;
  };

  const createLink = async () => {
    if (creatingRequest.current || !validateForm()) return;

    creatingRequest.current = true;
    setCreating(true);
    const paymentLinkBaseUrl =
      process.env.REACT_APP_PAYMENT_LINK_URL || process.env.REACT_APP_APP_URL;
    const body: any = {
      base_url: paymentLinkBaseUrl,
      description: form.description || 'Link de pagamento',
      dispatch: form.dispatch,
      due_date: form.dueDate || null,
      external_reference: form.externalReference || null,
      type: form.type,
      ...(form.type === 'amount' && { amount: Number(form.amount) }),
      ...(form.type === 'boleto' && { barcode: onlyDigits(form.barcode) }),
      ...(form.destinationValue && {
        destination: {
          type: form.destinationType,
          value:
            form.destinationType === 'Whatsapp'
              ? onlyDigits(form.destinationValue)
              : form.destinationValue,
        },
      }),
    };

    const [success, data] = await postApiPaymentLinksService(body);
    creatingRequest.current = false;
    setCreating(false);

    if (!success) {
      showToast(
        data?.message || data?.error || 'Falha ao criar link de pagamento.',
        'error'
      );
      return;
    }

    setForm(createInitialForm());
    setPage(0);
    if (form.dispatch && !data?.sent_at) {
      showToast(
        'Link criado, mas o envio não foi confirmado. Você pode reenviar pela lista.',
        'warning'
      );
    } else {
      showToast(
        form.dispatch ? 'Link criado e enviado.' : 'Link de pagamento criado.'
      );
    }
    setTab('list');
    await latestLoadLinks.current();
  };

  const copyLink = async (link: string) => {
    if (!link) return;

    if (navigator.clipboard?.writeText) {
      await navigator.clipboard.writeText(link);
    } else {
      const element = document.createElement('textarea');
      element.value = link;
      document.body.appendChild(element);
      element.select();
      document.execCommand('copy');
      document.body.removeChild(element);
    }
    showToast('Link copiado.');
  };

  const openDetail = async (item: any) => {
    const requestVersion = ++detailRequestVersion.current;
    activeDetailId.current = item.id;
    setDetail(item);
    setDetailOpen(true);
    setDetailLoading(true);
    const [success, data] = await getApiPaymentLinksIdService(item.id);
    if (requestVersion !== detailRequestVersion.current) return;
    setDetailLoading(false);
    if (!success) {
      showToast('Falha ao abrir detalhes do link.', 'error');
      return;
    }

    setDetail(withEffectivePaymentLinkStatus(data));
  };

  const closeDetail = () => {
    ++detailRequestVersion.current;
    activeDetailId.current = null;
    setDetailOpen(false);
    setDetailLoading(false);
  };

  const updateCurrentDetail = (id: string, version: number, data: any) => {
    if (
      activeDetailId.current !== id ||
      detailRequestVersion.current !== version
    )
      return;
    ++detailRequestVersion.current;
    setDetailLoading(false);
    setDetail(withEffectivePaymentLinkStatus(data));
  };

  const refreshStatus = async (item: any) => {
    if (refreshingRequest.current) return;
    refreshingRequest.current = true;
    const detailVersion = detailRequestVersion.current;
    setRefreshingId(item.id);
    const [success, data] = await postApiPaymentLinksIdRefreshStatusService(
      item.id
    );
    refreshingRequest.current = false;
    setRefreshingId('');

    if (!success) {
      showToast(
        data?.message || data?.error || 'Falha ao atualizar situação.',
        'error'
      );
      return;
    }

    showToast('Situação atualizada.');
    updateCurrentDetail(item.id, detailVersion, data);
    await latestLoadLinks.current();
  };

  const openSendDialog = (item: any) => {
    if (sendingRequest.current) return;
    const type = item.destination_type || 'Whatsapp';
    setSendDialog(item);
    setSendForm({
      type,
      value:
        type === 'Whatsapp'
          ? formatPhoneNumber(item.destination || '')
          : item.destination || '',
    });
  };

  const sendLink = async () => {
    if (sendingRequest.current) return;

    if (!sendDialog?.id || !sendForm.value) {
      showToast('Informe o destino para envio.', 'error');
      return;
    }

    if (
      sendForm.type === 'Email' &&
      !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(sendForm.value)
    ) {
      showToast('Informe um e-mail válido.', 'error');
      return;
    }

    if (sendForm.type === 'Whatsapp') {
      const phone = onlyDigits(sendForm.value);
      if (phone.length < 10 || phone.length > 11) {
        showToast('Informe um WhatsApp com DDD.', 'error');
        return;
      }
    }

    sendingRequest.current = true;
    const detailVersion = detailRequestVersion.current;
    setSending(true);
    const [success, data] = await postApiPaymentLinksIdDispatchService(
      sendDialog.id,
      {
        destination: {
          type: sendForm.type,
          value:
            sendForm.type === 'Whatsapp'
              ? onlyDigits(sendForm.value)
              : sendForm.value,
        },
      }
    );
    sendingRequest.current = false;
    setSending(false);

    if (!success) {
      showToast(
        data?.message || data?.error || 'Falha ao enviar link.',
        'error'
      );
      return;
    }

    setSendDialog(null);
    showToast('Link enviado.');
    updateCurrentDetail(sendDialog.id, detailVersion, data);
    await latestLoadLinks.current();
  };

  const openCheckout = (link: string) => {
    if (!link) return;
    if (!isSafeHttpUrl(link)) {
      showToast('O endereço do link de pagamento é inválido.', 'error');
      return;
    }
    window.open(link, '_blank', 'noopener,noreferrer');
  };

  const destinationHint =
    form.destinationType === 'Email'
      ? 'cliente@email.com'
      : 'DDD + número (ex.: 11999998888)';

  const summaryGroups = SUMMARY_GROUPS.map((group) => {
    const items = group.statuses
      ? links.filter((link) => group.statuses?.includes(link.status))
      : links;
    const tooltip = group.statuses
      ? `Inclui: ${group.statuses
          .map((status) => getStatusConfig(status).label)
          .join(', ')}. Indicador calculado sobre os links carregados.`
      : 'Todos os links carregados nos filtros atuais.';

    return {
      ...group,
      tooltip,
      count: items.length,
      amount: items.reduce(
        (total, link) => total + Number(link.amount || 0),
        0
      ),
    };
  });
  const summaryGroupByKey = summaryGroups.reduce<Record<string, any>>(
    (groups, group) => ({ ...groups, [group.key]: group }),
    {}
  );
  const quickFilterItems = [
    {
      key: 'total',
      label: 'Todos',
      count: summaryGroupByKey.total?.count || 0,
    },
    {
      key: 'waiting',
      label: 'Abertos',
      count: summaryGroupByKey.waiting?.count || 0,
    },
    {
      key: 'in_progress',
      label: 'Em análise',
      count: summaryGroupByKey.in_progress?.count || 0,
    },
    {
      key: 'expired',
      label: 'Expirados',
      count: links.filter((link) => link.status === 'expired').length,
    },
    {
      key: 'paid',
      label: 'Pagos',
      count: summaryGroupByKey.paid?.count || 0,
    },
    {
      key: 'failed',
      label: 'Não concluídos',
      count: summaryGroupByKey.failed?.count || 0,
    },
  ];

  const activeQuickStatusGroup = SUMMARY_GROUPS.find(
    (group) => group.key === quickStatusGroupKey
  );
  const statusFilteredLinks = activeQuickStatusGroup?.statuses
    ? links.filter((link) =>
        activeQuickStatusGroup.statuses?.includes(link.status)
      )
    : links;

  const filteredLinks = statusFilteredLinks;

  const applySummaryFilter = (groupKey: string) => {
    const group = SUMMARY_GROUPS.find((item) => item.key === groupKey);
    if (!group) return;

    setPage(0);
    setQuickStatusGroupKey(group.statuses ? group.key : '');
    setFilters((state) => (state.status ? { ...state, status: '' } : state));
  };

  const applyQuickFilter = (key: string) => {
    setPage(0);

    if (key === 'total') {
      setQuickStatusGroupKey('');
      setFilters((state) => (state.status ? { ...state, status: '' } : state));
      return;
    }

    const group = SUMMARY_GROUPS.find((item) => item.key === key);
    if (group?.statuses) {
      setQuickStatusGroupKey(group.key);
      setFilters((state) => (state.status ? { ...state, status: '' } : state));
      return;
    }

    setQuickStatusGroupKey('');
    setFilters((state) =>
      state.status === key ? state : { ...state, status: key }
    );
  };

  const quickFilterKey =
    quickStatusGroupKey ||
    (['created', 'open', 'identified'].includes(filters.status)
      ? 'waiting'
      : ['expired', 'paid'].includes(filters.status)
      ? filters.status
      : !filters.status
      ? 'total'
      : '');

  const exportCsv = () => exportLinksCsv(filteredLinks);

  const openActionMenu = (event: React.MouseEvent<HTMLElement>, item: any) => {
    setActionMenu({ anchorEl: event.currentTarget, item });
  };

  const closeActionMenu = () => {
    setActionMenu({ anchorEl: null, item: null });
  };

  // Executa a ação fechando o menu antes (mantém a referência do item da linha).
  const runAction = (action: (item: any) => void) => {
    const item = actionMenu.item;
    closeActionMenu();
    if (item) action(item);
  };

  const menuItem = actionMenu.item;
  const menuItemIsFinal = menuItem
    ? FINAL_STATUSES.includes(menuItem.status)
    : false;
  const menuItemCanSend = menuItem
    ? !NOT_SENDABLE_STATUSES.includes(menuItem.status)
    : false;

  const pageCount = Math.max(1, Math.ceil(filteredLinks.length / rowsPerPage));
  const currentPage = Math.min(page, pageCount - 1);
  React.useEffect(() => {
    setPage(currentPage);
  }, [currentPage]);
  const pagedLinks = filteredLinks.slice(
    currentPage * rowsPerPage,
    currentPage * rowsPerPage + rowsPerPage
  );

  return {
    actionMenu,
    clearFilters,
    closeActionMenu,
    closeDetail,
    copyLink,
    createLink,
    creating,
    destinationHint,
    detail,
    detailLoading,
    detailOpen,
    downloadLinkSummary,
    downloadQrCode,
    exportCsv,
    filters,
    filteredLinks,
    form,
    hasActiveFilters,
    links,
    loadMoreLinks,
    loadLinks,
    loading,
    loadingMore,
    menuItem,
    menuItemCanSend,
    menuItemIsFinal,
    openActionMenu,
    openCheckout,
    openDetail,
    openSendDialog,
    page: currentPage,
    pageCount,
    pagedLinks,
    quickFilterItems,
    quickStatusGroupKey,
    refreshLinks,
    refreshStatus,
    refreshingId,
    rowsPerPage,
    runAction,
    searchInput,
    sendDialog,
    sendForm,
    sending,
    sendLink,
    setForm,
    setPage,
    setRowsPerPage,
    setSearchInput,
    setSendDialog,
    setSendForm,
    setTab,
    summaryGroups,
    tab,
    totalAvailableLinks,
    updateFilter,
    updateForm,
    applySummaryFilter,
    applyQuickFilter,
    quickFilterKey,
  };
};

export default usePaymentLinks;
